import dayjs from "dayjs";

export const toFiniteNumber = (value) => {
  if (value == null || value === "") {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const calculateDelaySeconds = (start, end) => {
  const startValue = toFiniteNumber(start);
  const endValue = toFiniteNumber(end);

  if (startValue == null || endValue == null || endValue < startValue) {
    return null;
  }

  return endValue - startValue;
};

export const getTimestamp = (message, key) => {
  if (!message) return null;

  if (key === "delivered") {
    return toFiniteNumber(message.delivered || message.deliveryInfo?.created);
  }

  return toFiniteNumber(message[key]);
};

export const getDelayValue = (message, key) => {
  const existingValue = toFiniteNumber(message?.[key]);
  if (existingValue != null && existingValue >= 0) {
    return existingValue;
  }

  if (key === "queueDelaySeconds") {
    return calculateDelaySeconds(message?.received, message?.sent);
  }

  if (key === "deliveryDelaySeconds") {
    return calculateDelaySeconds(message?.sent, message?.delivered);
  }

  if (key === "totalDelaySeconds") {
    return calculateDelaySeconds(message?.received, message?.delivered);
  }

  return null;
};

export const formatTimestamp = (timestamp) => {
  const numericTimestamp = toFiniteNumber(timestamp);
  return numericTimestamp == null ? "N/A" : dayjs.unix(numericTimestamp).format("YYYY-MM-DD HH:mm:ss");
};

export const formatDelay = (seconds) => {
  const numericSeconds = toFiniteNumber(seconds);
  if (numericSeconds == null || numericSeconds < 0) return "N/A";
  if (numericSeconds < 60) return `${Math.round(numericSeconds)}s`;

  const minutes = Math.floor(numericSeconds / 60);
  const remainingSeconds = Math.round(numericSeconds % 60);
  return `${minutes}m ${remainingSeconds}s`;
};

export const getProviderName = (message) => {
  if (message?.provider) return String(message.provider).toUpperCase();
  if (message?.mobile && String(message.mobile).startsWith("26481")) return "SMPP";
  return "N/A";
};

export const getProviderStatusLabel = (message) => {
  const providerStatus = message?.providerStatus;
  if (providerStatus) return String(providerStatus).replaceAll("_", " ");
  if (Number(message?.deliveryStatus) === 1) return "Delivered";
  return "N/A";
};

export const getDeliveryState = (message) => {
  if (Number(message?.deliveryStatus) === 1) {
    return { label: "Delivered", colorScheme: "green", state: "delivered" };
  }

  if (message?.provider === "clickatell") {
    const providerStatus = String(message?.providerStatus || "ACCEPTED").toUpperCase();

    if (message?.providerTerminalFailure) {
      return { label: "Failed", colorScheme: "red", state: "failed" };
    }

    if (["ACCEPTED", "QUEUED", "SCHEDULED", "SENT_TO_SUPPLIER"].includes(providerStatus)) {
      return { label: "Pending / In Transit", colorScheme: "orange", state: "pending" };
    }

    return { label: providerStatus.replaceAll("_", " "), colorScheme: "gray", state: "pending" };
  }

  return { label: "Undelivered", colorScheme: "red", state: "failed" };
};

export const average = (values) => {
  const validValues = values.filter((value) => value != null && Number.isFinite(value));
  if (!validValues.length) return null;
  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
};

export const percentile = (values, percentileValue) => {
  const validValues = values
    .filter((value) => value != null && Number.isFinite(value))
    .sort((a, b) => a - b);

  if (!validValues.length) return null;

  const index = Math.min(
    validValues.length - 1,
    Math.max(0, Math.ceil((percentileValue / 100) * validValues.length) - 1)
  );

  return validValues[index];
};

export const getDelayStats = (messages = []) => {
  const queueDelays = messages.map((message) => getDelayValue(message, "queueDelaySeconds"));
  const deliveryDelays = messages.map((message) => getDelayValue(message, "deliveryDelaySeconds"));
  const totalDelays = messages.map((message) => getDelayValue(message, "totalDelaySeconds"));

  return {
    averageQueueDelaySeconds: average(queueDelays),
    p95QueueDelaySeconds: percentile(queueDelays, 95),
    averageDeliveryDelaySeconds: average(deliveryDelays),
    p95TotalDelaySeconds: percentile(totalDelays, 95),
  };
};

export const getMessageTimeline = (message) => ({
  received: getTimestamp(message, "received"),
  sent: getTimestamp(message, "sent") || getTimestamp(message, "created"),
  delivered: getTimestamp(message, "delivered"),
  providerUpdated: getTimestamp(message, "providerUpdatedAt"),
});

export const getMessageSortTimestamp = (message) =>
  getTimestamp(message, "received") || getTimestamp(message, "created") || 0;
